import { useEffect, useMemo, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { api } from '../api';
import {
  formatPts,
  formatBattleName,
  formatSeconds,
  timeAgo,
  isLive,
  exportCSV,
} from '../utils';
import Icon from '../components/Icon';

// Must match MEANINGFUL_STALL_SEC in server.js — a single missed scan interval
// is normal, only a sustained gap counts as an actual stall.
const MEANINGFUL_STALL_SEC = 1800;

const SORT_OPTIONS = [
  { value: 'points', label: 'Total Points' },
  { value: 'rankChange', label: 'Rank Change (climbers first)' },
  { value: 'lastGain', label: 'Last Gain (most recent first)' },
  { value: 'stallTime', label: 'Total Stall Time' },
  { value: 'stallCount', label: 'Stall Count' },
  { value: 'longest', label: 'Longest Stall' },
  { value: 'username', label: 'Username (A→Z)' },
];

const sortPlayers = (players, mode) => {
  const out = [...players];
  switch (mode) {
    case 'stallCount':
      return out.sort((a, b) => b.stallCount - a.stallCount || b.totalDisconnectedSeconds - a.totalDisconnectedSeconds);
    case 'points':
      return out.sort((a, b) => b.maxPoints - a.maxPoints);
    case 'rankChange':
      return out.sort((a, b) => b.rankChange - a.rankChange);
    case 'longest':
      return out.sort((a, b) => b.longestStallSeconds - a.longestStallSeconds);
    case 'lastGain':
      return out.sort((a, b) => {
        const ta = a.lastPointMovementAt ? new Date(a.lastPointMovementAt).getTime() : 0;
        const tb = b.lastPointMovementAt ? new Date(b.lastPointMovementAt).getTime() : 0;
        return tb - ta;
      });
    case 'username':
      return out.sort((a, b) => (a.username || '').localeCompare(b.username || ''));
    case 'stallTime':
    default:
      return out.sort((a, b) => b.totalDisconnectedSeconds - a.totalDisconnectedSeconds || b.stallCount - a.stallCount);
  }
};

export default function ClanBattleStats() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [clans, setClans]     = useState(null);
  const [battles, setBattles] = useState(null);
  const [data, setData]       = useState(null);
  const [formData, setFormData] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [tab, setTab]         = useState('overview');
  const [sortMode, setSortMode] = useState('points');
  const [filter, setFilter]   = useState('');
  const [onlyStalled, setOnlyStalled] = useState(false);
  const [clanIcon, setClanIcon] = useState(null);

  const clanId   = searchParams.get('clanId')   || '';
  const battleId = searchParams.get('battleId') || '';

  // Load clans + battles (and seed selection from current battle if none chosen)
  useEffect(() => {
    Promise.all([api.clans(), api.battles(), api.currentBattle().catch(() => null)])
      .then(([c, b, cur]) => {
        setClans(c);
        setBattles(b);
        if (!battleId && cur?.id) {
          setSearchParams(prev => {
            const p = new URLSearchParams(prev);
            p.set('battleId', String(cur.id));
            return p;
          }, { replace: true });
        }
      })
      .catch(err => setError(err.message));
    // run once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch detail when both selected
  useEffect(() => {
    if (!clanId || !battleId) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(null);
    setClanIcon(null);
    api.clanBattleDetail(clanId, battleId)
      .then(d => {
        setData(d);
        setLoading(false);
        api.clanInfo(d.clan?.name, d.battle?.clanBattleName)
          .then(info => { if (info?.icon) setClanIcon(info.icon); })
          .catch(() => {});
      })
      .catch(err => { setError(err.message); setLoading(false); setData(null); });
  }, [clanId, battleId]);

  useEffect(() => {
    if (!clanId) {
      setFormData(null);
      return;
    }
    setFormLoading(true);
    api.clanForm(clanId, 5)
      .then(d => { setFormData(d); setFormLoading(false); })
      .catch(() => { setFormData(null); setFormLoading(false); });
  }, [clanId]);

  const updateParam = (key, value) => {
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      if (value) p.set(key, value);
      else p.delete(key);
      return p;
    });
  };

  // Filtered + sorted player list
  const visiblePlayers = useMemo(() => {
    if (!data?.players) return [];
    const q = filter.toLowerCase();
    let rows = data.players.filter(p => p.maxPoints > 0);
    if (q) rows = rows.filter(p => (p.username || '').toLowerCase().includes(q));
    if (onlyStalled) rows = rows.filter(p => p.longestStallSeconds >= MEANINGFUL_STALL_SEC);
    return sortPlayers(rows, sortMode);
  }, [data, filter, onlyStalled, sortMode]);

  const doExport = () => {
    if (!data) return;
    exportCSV(
      [
        ['Rank', 'Username', 'Roblox ID', 'Points', 'Stall Count', 'Total Stall Seconds', 'Longest Stall Seconds', 'Currently Stalled', 'Last Gain'],
        ...visiblePlayers.map((p, i) => [
          i + 1,
          p.username,
          p.robloxId,
          p.maxPoints,
          p.stallCount,
          p.totalDisconnectedSeconds,
          p.longestStallSeconds,
          p.currentlyStalled ? 'yes' : 'no',
          p.lastPointMovementAt ? new Date(p.lastPointMovementAt).toISOString() : '',
        ]),
      ],
      `${data.clan?.name ?? 'clan'}-${data.battle?.clanBattleName ?? 'battle'}-stalls.csv`
    );
  };

  // ── Render ────────────────────────────────────────────────────────────
  if (!clans || !battles) {
    return error
      ? <div className="page"><div className="error-box">{error}</div></div>
      : <div className="page-center"><div className="spinner" /></div>;
  }

  const battle = data?.battle;
  const clan   = data?.clan;
  const live   = battle && isLive(battle);
  const sum    = data?.summary;
  // Auto-tracked top-50 clans only get hourly snapshots — stall stats are noise there.
  const hourly = data?.tracking === 'hourly';
  const topPlayers = formData?.players?.slice(0, 5) || [];
  const bottomPlayers = formData?.players
    ? [...formData.players]
        .filter(p => p.currentMember && p.appearances >= 3 && p.appearances <= 5)
        .sort((a, b) =>
          a.formScore - b.formScore
          || a.appearances - b.appearances
          || (b.averageFinish ?? Number.MAX_SAFE_INTEGER) - (a.averageFinish ?? Number.MAX_SAFE_INTEGER)
          || (a.username || '').localeCompare(b.username || '')
        )
        .slice(0, 5)
    : [];
  const fiveBattleById = new Map((formData?.players || []).map(p => [String(p.robloxId), p]));

  return (
    <div className="page fade-in">
      {battleId && (
        <button className="btn" style={{ marginBottom: 16 }} onClick={() => navigate(`/battle/${battleId}`)}>
          ← Back to Battle
        </button>
      )}

      {/* Picker — front and center until a clan + battle are chosen */}
      {(!clanId || !battleId) && (
        <>
          <div className="page-header">
            <div>
              <div className="page-header-title">Clan Stats</div>
              <div className="page-header-sub">
                Per-player breakdown, stall analysis, and battle standing for a clan.
              </div>
            </div>
          </div>
          <div className="filter-bar" style={{ marginBottom: 16 }}>
            <select value={clanId} onChange={e => updateParam('clanId', e.target.value)} style={{ minWidth: 200 }}>
              <option value="">— Select clan —</option>
              {clans.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <select value={battleId} onChange={e => updateParam('battleId', e.target.value)} style={{ minWidth: 220 }}>
              <option value="">— Select battle —</option>
              {battles.map(b => (
                <option key={b.id} value={b.id}>
                  {formatBattleName(b.clanBattleName)}{!b.finished ? ' (Live)' : ''}
                </option>
              ))}
            </select>
          </div>
          <div className="empty-msg">👆 Pick a clan and a battle to see the full breakdown.</div>
        </>
      )}

      {/* Compact switcher once a clan + battle are loaded */}
      {clanId && battleId && (
        <div className="filter-bar" style={{ marginBottom: 16, justifyContent: 'flex-end' }}>
          <select value={clanId} onChange={e => updateParam('clanId', e.target.value)} style={{ minWidth: 160 }}>
            {clans.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <select value={battleId} onChange={e => updateParam('battleId', e.target.value)} style={{ minWidth: 180 }}>
            {battles.map(b => (
              <option key={b.id} value={b.id}>
                {formatBattleName(b.clanBattleName)}{!b.finished ? ' (Live)' : ''}
              </option>
            ))}
          </select>
          <button className="btn" onClick={doExport} disabled={!data}>⬇ CSV</button>
        </div>
      )}

      {error && (clanId && battleId) && (
        <div className="error-box">{error}</div>
      )}

      {loading && <div className="page-center"><div className="spinner" /></div>}

      {/* Detail */}
      {!loading && data && clan && battle && sum && (
        <>
          {/* Hero */}
          <div className="hero">
            <div className="hero-top">
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                {clanIcon
                  ? <img className="clan-icon" style={{ width: 48, height: 48 }} src={clanIcon} alt="" />
                  : <div className="clan-icon-ph" style={{ width: 48, height: 48, fontSize: '1.2rem' }}>{clan.name[0]?.toUpperCase()}</div>}
                <div>
                  <div className="hero-label">Welcome to the war room</div>
                  <div className="hero-title">{clan.name}</div>
                  <div className="clan-greeting">Five completed battles of form, the current standing, and the full active roster.</div>
                  <div className="hero-meta">
                  <button
                    className="btn"
                    style={{ padding: '4px 10px', fontSize: '.78rem' }}
                    onClick={() => navigate(`/battle/${battle.id}`)}
                  >
                    {formatBattleName(battle.clanBattleName)} →
                  </button>
                  {live
                    ? <span className="badge badge-live">Live</span>
                    : <span className="badge badge-done">Finished</span>}
                  {sum.rank && (
                    <span className="badge badge-amber">
                      Rank #{sum.rank} of {sum.totalClans}
                    </span>
                  )}
                  {hourly && <span className="badge">⏱ hourly snapshots</span>}
                  </div>
                </div>
              </div>

              <div className="hero-right">
                <div className="stat-card">
                  <div className="stat-val amber">{formatPts(sum.totalPoints)}</div>
                  <div className="stat-lbl">Total Points</div>
                </div>
                <div className="stat-card">
                  <div className="stat-val blue">{sum.playerCount}</div>
                  <div className="stat-lbl">Players</div>
                </div>
                {sum.aboveGap != null && (
                  <div className="stat-card">
                    <div className="stat-val">↑ {formatPts(sum.aboveGap)}</div>
                    <div className="stat-lbl">To Rank #{sum.rank - 1}</div>
                  </div>
                )}
                {sum.belowGap != null && (
                  <div className="stat-card">
                    <div className="stat-val">↓ {formatPts(sum.belowGap)}</div>
                    <div className="stat-lbl">Lead Over #{sum.rank + 1}</div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="tabs">
            {[
              ['overview', 'Overview'],
              ['players',  `Full roster (${sum.playerCount})`],
              ...(hourly ? [] : [['stalls', `Connection health (${sum.playersWithStalls})`]]),
            ].map(([id, label]) => (
              <button
                key={id}
                className={`tab-btn${tab === id ? ' active' : ''}`}
                onClick={() => {
                  setTab(id);
                  if (id === 'players') setSortMode('points');
                  if (id === 'stalls') setSortMode('stallTime');
                }}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Overview tab */}
          {tab === 'overview' && (
            <div className="clan-overview">
              <section className="top-squad">
                <div className="top-squad-heading">
                  <div>
                    <div className="eyebrow">Top five overall</div>
                    <h2>The players carrying the flag</h2>
                    <p>
                      Placement percentile across {formData?.battleCount || 0} completed
                      battle{formData?.battleCount === 1 ? '' : 's'} · missed battles score zero.
                    </p>
                  </div>
                  <button className="text-action" onClick={() => setTab('players')}>
                    Open full roster <Icon name="arrow" size={15} />
                  </button>
                </div>

                {formLoading ? (
                  <div className="page-center top-five-loading"><div className="spinner spinner-sm" /> Building five-battle form…</div>
                ) : topPlayers.length > 0 ? (
                  <div className="top-squad-list">
                    {topPlayers.map((player, index) => (
                        <button
                          className={`top-player${index === 0 ? ' leader' : ''}${!player.currentMember ? ' former' : ''}`}
                          key={player.robloxId}
                          onClick={() => navigate(`/player/${player.robloxId}?battleId=${battleId}`)}
                        >
                          <span className="top-player-place">{String(index + 1).padStart(2, '0')}</span>
                          <span className="top-player-avatar">{player.username?.[0]?.toUpperCase()}</span>
                          <span className="top-player-name">
                            <strong>{player.username}</strong>
                            <small>
                              {!player.currentMember
                                ? 'Former member'
                                : index === 0
                                  ? 'Five-battle MVP'
                                  : `Best clan finish #${player.bestFinish}`}
                            </small>
                          </span>
                          <span className="top-player-finishes">
                            {formData.battles.map(b => {
                              const finish = player.battleScores.find(s => String(s.battleId) === String(b.battleId));
                              return (
                                <i
                                  key={b.battleId}
                                  className={finish ? (finish.clanRank <= 3 ? ' podium' : '') : ' missed'}
                                  title={formatBattleName(b.battleName)}
                                >
                                  {finish ? `#${finish.clanRank}` : '—'}
                                </i>
                              );
                            })}
                            <small>newest → oldest</small>
                          </span>
                          <span className="top-player-form">
                            <strong>{player.appearances}/{formData.battleCount}</strong>
                            <small>appearances</small>
                          </span>
                          <span className="top-player-score">
                            <strong>{player.formScore.toFixed(1)}</strong>
                            <small>form / 100</small>
                          </span>
                          <Icon name="arrow" size={16} />
                        </button>
                    ))}
                  </div>
                ) : (
                  <div className="empty-msg">No player scores have been recorded yet.</div>
                )}

                {!formLoading && bottomPlayers.length > 0 && (
                  <div className="bottom-squad">
                    <div className="bottom-squad-heading">
                      <div>
                        <div className="eyebrow">Bottom five form</div>
                        <h3>Players who need a rally</h3>
                      </div>
                      <span>Current members · 3–5 appearances</span>
                    </div>
                    <div className="top-squad-list bottom-squad-list">
                      {bottomPlayers.map((player, index) => (
                        <button
                          className="top-player bottom-player"
                          key={player.robloxId}
                          onClick={() => navigate(`/player/${player.robloxId}?battleId=${battleId}`)}
                        >
                          <span className="top-player-place">{String(index + 1).padStart(2, '0')}</span>
                          <span className="top-player-avatar">{player.username?.[0]?.toUpperCase()}</span>
                          <span className="top-player-name">
                            <strong>{player.username}</strong>
                            <small>{player.appearances === 0 ? 'No appearances' : `Average finish #${player.averageFinish}`}</small>
                          </span>
                          <span className="top-player-finishes">
                            {formData.battles.map(b => {
                              const finish = player.battleScores.find(s => String(s.battleId) === String(b.battleId));
                              return (
                                <i
                                  key={b.battleId}
                                  className={finish ? (finish.clanRank <= 3 ? ' podium' : '') : ' missed'}
                                  title={formatBattleName(b.battleName)}
                                >
                                  {finish ? `#${finish.clanRank}` : '—'}
                                </i>
                              );
                            })}
                            <small>newest → oldest</small>
                          </span>
                          <span className="top-player-form">
                            <strong>{player.appearances}/{formData.battleCount}</strong>
                            <small>appearances</small>
                          </span>
                          <span className="top-player-score">
                            <strong>{player.formScore.toFixed(1)}</strong>
                            <small>form / 100</small>
                          </span>
                          <Icon name="arrow" size={16} />
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </section>

              <aside className="clan-pulse">
                <div className="eyebrow">Battle pulse</div>
                <h3>Where the clan stands</h3>
                <div className="pulse-rank">
                  <span>Current position</span>
                  <strong>{sum.rank ? `#${sum.rank}` : '—'}</strong>
                  <small>of {sum.totalClans} clans</small>
                </div>
                <div className="pulse-list">
                  <div><span>Total score</span><strong>{formatPts(sum.totalPoints)}</strong></div>
                  <div><span>Active roster</span><strong>{sum.playerCount} players</strong></div>
                  <div><span>Gap ahead</span><strong>{sum.aboveGap != null ? formatPts(sum.aboveGap) : 'Leading'}</strong></div>
                  <div><span>Lead behind</span><strong>{sum.belowGap != null ? formatPts(sum.belowGap) : '—'}</strong></div>
                  <div><span>Last scan</span><strong>{sum.lastUpdate ? timeAgo(sum.lastUpdate) : '—'}</strong></div>
                </div>
                {!hourly && sum.currentlyStalled > 0 && (
                  <button className="pulse-alert" onClick={() => setTab('stalls')}>
                    <Icon name="bolt" size={16} />
                    <span><strong>{sum.currentlyStalled} connection alert{sum.currentlyStalled === 1 ? '' : 's'}</strong><small>Review connection health</small></span>
                    <Icon name="arrow" size={15} />
                  </button>
                )}
              </aside>
            </div>
          )}

          {/* Stalls tab */}
          {(tab === 'stalls' || tab === 'players') && (
            <>
              <div className="filter-bar">
                <input
                  className="filter-input"
                  placeholder="Filter by player name…"
                  value={filter}
                  onChange={e => setFilter(e.target.value)}
                />
                <select value={sortMode} onChange={e => setSortMode(e.target.value)}>
                  {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>Sort: {o.label}</option>)}
                </select>
                {tab === 'players' && !hourly && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.85rem', color: 'var(--muted)' }}>
                    <input
                      type="checkbox"
                      checked={onlyStalled}
                      onChange={e => setOnlyStalled(e.target.checked)}
                    />
                    connection issues only
                  </label>
                )}
              </div>

              {(() => {
                const rows = tab === 'stalls'
                  ? visiblePlayers.filter(p => p.longestStallSeconds >= MEANINGFUL_STALL_SEC)
                  : visiblePlayers;

                if (rows.length === 0) {
                  return (
                    <div className="empty-msg">
                      {tab === 'stalls'
                        ? '✅ No stalls recorded for this clan in this battle.'
                        : 'No players match your filter.'}
                    </div>
                  );
                }

                return (
                  <div className={`roster-board${hourly ? ' hourly' : ''}`}>
                    <div className="roster-board-head">
                      <div>
                        <strong>{tab === 'players' ? 'Active battle roster' : 'Connection review'}</strong>
                        <span>
                          {tab === 'players'
                            ? 'Current output, recent movement, historical form, and reliability.'
                            : 'Players with meaningful connection interruptions in this battle.'}
                        </span>
                      </div>
                      <div className="roster-view-count">
                        <strong>{rows.length}</strong>
                        <span>players shown</span>
                      </div>
                    </div>
                    <div className="roster-detail-head">
                      <span>Player</span>
                      <span>Battle output</span>
                      <span>Recent activity</span>
                      <span>Five-battle form</span>
                      {!hourly && <span>Reliability</span>}
                      <span />
                    </div>
                    <div className="roster-detail-list">
                      {rows.map(p => {
                        const form = fiveBattleById.get(String(p.robloxId));
                        return (
                          <button
                            className={`roster-detail-row${p.currentlyStalled ? ' is-stalled' : ''}`}
                            key={p.playerId}
                            onClick={() => navigate(`/player/${p.robloxId}?battleId=${battleId}`)}
                          >
                            <div className="roster-player-cell">
                              <span className={`roster-rank${p.clanRank <= 3 ? ' podium' : ''}`}>#{p.clanRank}</span>
                              <div className="roster-avatar">{p.username?.[0]?.toUpperCase()}</div>
                              <div className="roster-name">
                                <strong>{p.username}</strong>
                                <span>Roblox ID {p.robloxId}</span>
                              </div>
                            </div>

                            <div className="roster-current-cell">
                              <span className="roster-cell-label">Current score</span>
                              <div className="roster-battle-score">
                                <strong>{formatPts(p.maxPoints)}</strong>
                                <small>points</small>
                              </div>
                            </div>

                            <div className="roster-momentum-cell">
                              <div><span>24h movement</span><RankDelta change={p.rankChange} /></div>
                              <div>
                                <span>Last contribution</span>
                                <strong>{p.lastPointMovementAt ? timeAgo(p.lastPointMovementAt) : 'No movement'}</strong>
                              </div>
                            </div>

                            <div className="roster-form-cell">
                              {form ? (
                                <>
                                  <div className="form-score-ring">
                                    <strong>{form.formScore.toFixed(1)}</strong>
                                    <span>/ 100</span>
                                  </div>
                                  <div className="form-context">
                                    <span>{form.appearances}/{formData.battleCount} appearances</span>
                                    <strong>{form.averageFinish != null ? `#${form.averageFinish} average finish` : 'No recent finishes'}</strong>
                                  </div>
                                </>
                              ) : <span className="form-empty">No recent form</span>}
                            </div>

                            {!hourly && (
                              <div className="roster-health-cell">
                                <div className="health-status">
                                  <span className={`health-dot${p.currentlyStalled ? ' down' : ''}`} />
                                  <strong>{p.currentlyStalled ? 'Disconnected' : 'Connected'}</strong>
                                </div>
                                <div className="health-details">
                                  <span>{p.stallCount} interruption{p.stallCount === 1 ? '' : 's'}</span>
                                  <span>{p.totalDisconnectedSeconds > 0 ? formatSeconds(p.totalDisconnectedSeconds) : 'No downtime'}</span>
                                </div>
                                {p.longestStallSeconds > 0 && <small>Longest {formatSeconds(p.longestStallSeconds)}</small>}
                              </div>
                            )}

                            <Icon name="arrow" size={17} className="roster-open" />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
            </>
          )}
        </>
      )}
    </div>
  );
}

function RankDelta({ change }) {
  if (!change) return <span style={{ color: 'var(--muted)', fontSize: '.85rem' }}>—</span>;
  const up = change > 0;
  return (
    <span style={{ fontWeight: 700, fontSize: '.85rem', color: up ? 'var(--green)' : 'var(--red)' }}>
      {up ? '↑' : '↓'} {Math.abs(change)}
    </span>
  );
}
